import { Box, IconCopy, IconTick, Text } from 'braid-design-system';
import { type ComponentProps, useState } from 'react';

import { AutoLinkedInlineCode } from '#src/components/AutoLinkedInlineCode/AutoLinkedInlineCode.tsx';

import * as styles from './CopyableText.css';
import { useTracking } from '#src/hooks/tracking/TrackingProvider.tsx';
import { useElementPosition } from '#src/hooks/ElementPositionProvider.tsx';

interface CopyableTextProps extends Omit<
  ComponentProps<typeof Text>,
  'children'
> {
  inlineCode?: boolean;
  children: string | number;
  value?: string | number;
}

export const CopyableText = ({
  children,
  inlineCode,
  value,
  ...restProps
}: CopyableTextProps) => {
  const [copied, setCopied] = useState<boolean>(false);
  const { trackLink } = useTracking();
  const elementPosition = useElementPosition();

  const copyText = async () => {
    if (copied) {
      return;
    }

    setCopied(true);

    const valueToCopy = String(value ?? children);

    await navigator.clipboard.writeText(valueToCopy);

    trackLink({
      eventName: 'developer_site_element_pressed',
      data: {
        elementAction: 'copy',
        elementText: 'icon',
        elementPosition,
        elementDesign: 'button',
      },
    });

    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Text {...restProps}>
      <Box
        className={styles.copyableText}
        component="span"
        paddingRight="xsmall"
      >
        {inlineCode ? (
          <AutoLinkedInlineCode>{children}</AutoLinkedInlineCode>
        ) : (
          children
        )}{' '}
        <Box
          className={!copied && styles.copyButton}
          component="span"
          cursor="pointer"
          onClick={copyText}
          title="Copy to Clipboard"
        >
          {copied ? <IconTick tone="positive" /> : <IconCopy tone="link" />}
        </Box>
      </Box>
    </Text>
  );
};
